/**
 * Size Guide Modal — global controller
 * ------------------------------------
 * Lives alongside snippets/size-guide-modal.liquid and assets/size-guide.css.
 *
 * Open triggers (any element on the site, no per-instance wiring needed):
 *   • <a href="#size-guide">…</a>      — primary, deep-linkable, JS-free fallback
 *   • <button data-open-size-guide>    — for non-anchor triggers
 *   • Loading any URL with #size-guide — auto-opens on DOMContentLoaded
 *
 * Close triggers:
 *   • [data-close-size-guide] click    — close button inside the modal
 *   • Backdrop click (panel outside)
 *   • Escape key
 *
 * Side effects:
 *   • Adds .sg-modal-open to <body> for scroll lock
 *   • Updates aria-hidden on the modal
 *   • Returns focus to the trigger element on close
 *   • Manages URL hash (clean-up on close)
 *
 * Size tile "Select" CTA:
 *   • Each .sg-vcard carries data-sg-size (1–6) and data-sg-size-value
 *     (the exact Size option value, e.g. "Size 3 (Height 5'6 to 5'8)").
 *   • Clicking a tile / its Select button on the product page sets the
 *     Size dropdown and fires a normal change event (theme + placeholder
 *     guard react exactly as if the user picked it), closes the modal,
 *     and scrolls to the variant picker.
 *   • On any other page it redirects to the product URL (from the modal's
 *     data-sg-product-url) with ?sg_size=N, which this script applies on
 *     load, then cleans from the URL.
 */
(function () {
  'use strict';

  var MODAL_ID = 'size-guide-modal';
  var HASH = '#size-guide';

  var modal = null;
  var lastTrigger = null;
  var transitionDuration = 240;

  function getModal() {
    if (!modal) modal = document.getElementById(MODAL_ID);
    return modal;
  }

  function getFocusables(container) {
    return Array.prototype.slice.call(
      container.querySelectorAll(
        'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function openModal(trigger) {
    var m = getModal();
    if (!m || m.classList.contains('is-open')) return;

    lastTrigger = trigger || null;

    m.hidden = false;
    m.setAttribute('aria-hidden', 'false');
    // Force reflow so the opacity transition fires
    void m.offsetWidth;
    m.classList.add('is-open');
    document.body.classList.add('sg-modal-open');

    // Focus the close button so Esc / Tab is intuitive
    var closeBtn = m.querySelector('[data-close-size-guide]');
    if (closeBtn) {
      // Tiny delay so the panel transition starts before focus moves
      setTimeout(function () { closeBtn.focus(); }, 50);
    }

    // Reflect state in the URL hash without scrolling
    if (window.location.hash !== HASH) {
      try {
        history.replaceState(null, '', window.location.pathname + window.location.search + HASH);
      } catch (e) { /* old browsers — ignore */ }
    }
  }

  function closeModal() {
    var m = getModal();
    if (!m || !m.classList.contains('is-open')) return;

    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sg-modal-open');

    // Hide after the transition completes
    setTimeout(function () {
      if (!m.classList.contains('is-open')) m.hidden = true;
    }, transitionDuration);

    // Clean the hash so the URL doesn't keep saying #size-guide
    if (window.location.hash === HASH) {
      try {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch (e) { /* old browsers — ignore */ }
    }

    // Restore focus to the element that opened the modal
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
    lastTrigger = null;
  }

  /* ---------------- Size tile "Select" CTA ---------------- */

  // The size dropdown rendered by snippets/product-variant-picker.liquid.
  // Matched by its option VALUES ("Size 1 …", "Size 2 …") rather than the
  // option's name attribute — the product option is currently named
  // "Mat Size", and this survives renames.
  function getSizeSelect() {
    var selects = document.querySelectorAll('variant-selects select');
    for (var i = 0; i < selects.length; i++) {
      var opts = selects[i].options;
      for (var j = 0; j < opts.length; j++) {
        if (/^size\s*\d/i.test(opts[j].value.trim())) return selects[i];
      }
    }
    return null;
  }

  // Find the <option> for a given tile: exact value match first, then a
  // "Size N" prefix match, skipping the "Select Size" placeholder.
  function findSizeOptionEl(select, sizeNum, sizeValue) {
    var options = Array.prototype.slice.call(select.options);
    var match = null;
    if (sizeValue) {
      match = options.filter(function (o) { return o.value === sizeValue; })[0];
      if (match) return match;
    }
    var prefix = new RegExp('^size\\s*' + sizeNum + '\\b', 'i');
    match = options.filter(function (o) { return prefix.test(o.value.trim()); })[0];
    return match || null;
  }

  function isOnProductPage(productUrl) {
    var path = (productUrl || '').split('?')[0].split('#')[0];
    if (!path) return false;
    return window.location.pathname.indexOf(path) !== -1;
  }

  function applySize(select, optionEl, scroll) {
    select.value = optionEl.value;
    // Fire a normal change so variant-selects / product-info / the
    // placeholder-variant-guard all react as if the user picked it.
    select.dispatchEvent(new Event('change', { bubbles: true }));
    if (scroll) {
      setTimeout(function () {
        var target = select.closest('variant-selects') || select;
        try {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (err) {
          target.scrollIntoView();
        }
      }, transitionDuration + 60);
    }
  }

  function handleTileSelect(tile) {
    var sizeNum = tile.getAttribute('data-sg-size');
    var sizeValue = tile.getAttribute('data-sg-size-value') || '';
    var m = getModal();
    var productUrl = (m && m.getAttribute('data-sg-product-url')) || '/products/stance-iq-full-swing';

    if (isOnProductPage(productUrl)) {
      var select = getSizeSelect();
      var optionEl = select ? findSizeOptionEl(select, sizeNum, sizeValue) : null;
      if (optionEl) {
        closeModal();
        applySize(select, optionEl, true);
        return;
      }
    }

    // Not on the product page (or picker not found) — redirect and let the
    // load handler below apply the size.
    var joiner = productUrl.indexOf('?') === -1 ? '?' : '&';
    window.location.href = productUrl + joiner + 'sg_size=' + encodeURIComponent(sizeNum);
  }

  // Apply ?sg_size=N on page load (arrival from a redirect), then clean the URL
  function applySizeFromUrl() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (err) {
      return; // very old browsers — skip
    }
    var sizeNum = params.get('sg_size');
    if (!sizeNum) return;

    var select = getSizeSelect();
    var optionEl = select ? findSizeOptionEl(select, sizeNum, '') : null;
    if (optionEl) {
      applySize(select, optionEl, true);
    }

    params.delete('sg_size');
    var qs = params.toString();
    try {
      history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    } catch (err) { /* old browsers — ignore */ }
  }

  // Delegated click handler — works for any open trigger anywhere on the page
  function onDocumentClick(e) {
    // Size tile / Select button inside the modal
    var tile = e.target.closest && e.target.closest('.sg-vcard[data-sg-size]');
    if (tile) {
      e.preventDefault();
      handleTileSelect(tile);
      return;
    }

    // Open via [data-open-size-guide]
    var openBtn = e.target.closest && e.target.closest('[data-open-size-guide]');
    if (openBtn) {
      e.preventDefault();
      openModal(openBtn);
      return;
    }

    // Open via <a href="#size-guide"> — covers same-page anchors AND links
    // to the modal hash from other elements (footer links, etc.)
    var anchor = e.target.closest && e.target.closest('a[href]');
    if (anchor) {
      var href = anchor.getAttribute('href') || '';
      // Match "#size-guide" or "/path#size-guide" on the current origin
      var hashIdx = href.indexOf(HASH);
      if (hashIdx !== -1) {
        // Only intercept if the path matches current page (or is empty/relative #)
        var isSamePage = href === HASH ||
                         href.startsWith(HASH + '?') ||
                         href.startsWith(window.location.pathname + HASH) ||
                         (anchor.host === window.location.host &&
                          anchor.pathname === window.location.pathname);
        if (isSamePage) {
          e.preventDefault();
          openModal(anchor);
          return;
        }
        // Otherwise let it navigate; the destination page's load handler picks up the hash
      }
    }

    // Close via [data-close-size-guide]
    var closeBtn = e.target.closest && e.target.closest('[data-close-size-guide]');
    if (closeBtn) {
      e.preventDefault();
      closeModal();
      return;
    }

    // Close via backdrop click (clicking the dim background, not the panel)
    var m = getModal();
    if (m && m.classList.contains('is-open') && e.target === m) {
      closeModal();
    }
  }

  function onKeydown(e) {
    var m = getModal();
    if (!m || !m.classList.contains('is-open')) return;

    if (e.key === 'Escape' || e.keyCode === 27) {
      e.preventDefault();
      closeModal();
      return;
    }

    // Simple focus trap — keep Tab inside the modal
    if (e.key === 'Tab' || e.keyCode === 9) {
      var focusables = getFocusables(m);
      if (focusables.length === 0) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function onHashChange() {
    if (window.location.hash === HASH) {
      openModal(null);
    } else {
      // Don't auto-close if the user manually cleared the hash via close,
      // but DO close if the hash changed away from #size-guide externally.
      var m = getModal();
      if (m && m.classList.contains('is-open')) closeModal();
    }
  }

  function init() {
    if (!getModal()) return; // Snippet not on page
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('hashchange', onHashChange);

    // Auto-open on initial load if URL says so
    if (window.location.hash === HASH) {
      openModal(null);
    }

    // Apply a size handed over via ?sg_size=N (redirect from the modal)
    applySizeFromUrl();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
